import { Location } from '@angular/common';
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { ViewportService } from '../viewport/viewport.service';

/**
 * Edge-swipe "back" gesture for the mobile shell.
 *
 * Dragging from the very left edge of the screen towards the right pops history
 * (`location.back()`), exactly like the header back button ({@link BackDirective}).
 * Because it goes through real history, {@link PageTransitionService} reads the
 * resulting `popstate` and plays the backward slide — so the gesture reuses the
 * whole existing transition pipeline rather than animating anything itself.
 *
 * Gesture feedback is a small chevron "puck" that follows the finger and fills
 * as you pull. We deliberately do NOT drag the live page: the routed node is
 * swapped on navigation and the destination isn't rendered until commit, so a
 * page-drag would reveal blank shell and fight the View Transition snapshot.
 *
 * Scope: mobile viewport only (`ViewportService.isMobile`) and only while the
 * mobile app shell (`.sd-screen`) is mounted — desktop, auth and marketing pages
 * are untouched. Opt a sub-tree out with `[data-no-swipe-back]` (e.g. a
 * horizontal carousel that owns left-edge drags).
 */
@Injectable({ providedIn: 'root' })
export class SwipeBackService {
  private readonly viewport = inject(ViewportService);
  private readonly location = inject(Location);
  private readonly doc = inject(DOCUMENT);

  /** Touch must begin within this many px of the left edge to arm the gesture. */
  private readonly EDGE_PX = 28;
  /** Movement (px) before we lock in horizontal-vs-vertical intent. */
  private readonly SLOP_PX = 8;
  /** Pull distance that counts as a full commit (capped fraction of width). */
  private readonly commitPx = () => Math.min(this.width * 0.35, 160);
  /** A fast flick commits even if short. */
  private readonly FLICK_VELOCITY = 0.5; // px/ms
  private readonly FLICK_MIN_PX = 40;

  /**
   * Open overlays that should swallow the gesture instead of navigating back.
   * These markers only exist in the DOM while a sheet/modal is open (rendered
   * under an `@if`), so their mere presence is the "an overlay is up" signal.
   * `.cdk-overlay-pane` covers CDK dialogs (its container is always present, the
   * pane only when something is open).
   */
  private readonly OVERLAY_SELECTOR =
    '.sd-sheet-backdrop, .sheet-backdrop, .modal-backdrop, .dialog-backdrop, .backdrop, .cdk-overlay-pane';

  private startX = 0;
  private startY = 0;
  private width = 0;
  /** Armed at the edge, horizontal-vs-vertical intent not yet decided. */
  private tracking = false;
  /** Confirmed horizontal back-drag — we own the gesture and block scrolling. */
  private dragging = false;
  private progress = 0;
  private lastX = 0;
  private lastT = 0;
  private velocity = 0;

  private puck?: HTMLElement;

  init(): void {
    if (typeof window === 'undefined') return;
    // touchmove must be non-passive so we can preventDefault once we own the drag.
    this.doc.addEventListener('touchstart', this.onStart, { passive: true });
    this.doc.addEventListener('touchmove', this.onMove, { passive: false });
    this.doc.addEventListener('touchend', this.onEnd, { passive: true });
    this.doc.addEventListener('touchcancel', this.onEnd, { passive: true });
  }

  private readonly onStart = (e: TouchEvent): void => {
    this.tracking = false;
    this.dragging = false;
    if (e.touches.length !== 1) return;
    if (!this.viewport.isMobile()) return;
    if (history.length <= 1) return; // nothing to go back to

    const t = e.touches[0];
    if (t.clientX > this.EDGE_PX) return;

    const screen = this.doc.querySelector<HTMLElement>('.sd-screen');
    if (!screen) return; // not inside the mobile app shell

    // Let overlays / opted-out subtrees own left-edge drags.
    const target = e.target as Element | null;
    if (target?.closest('[data-no-swipe-back]')) return;
    if (this.doc.querySelector(this.OVERLAY_SELECTOR)) return;

    this.startX = this.lastX = t.clientX;
    this.startY = t.clientY;
    this.lastT = e.timeStamp;
    this.velocity = 0;
    this.width = screen.clientWidth || window.innerWidth;
    this.tracking = true;
    this.ensurePuck(screen);
  };

  private readonly onMove = (e: TouchEvent): void => {
    if (!this.tracking) return;
    const t = e.touches[0];
    const dx = t.clientX - this.startX;
    const dy = t.clientY - this.startY;

    if (!this.dragging) {
      // Decide intent once we've moved past the slop in either axis.
      if (Math.abs(dx) < this.SLOP_PX && Math.abs(dy) < this.SLOP_PX) return;
      if (dx <= 0 || Math.abs(dy) > Math.abs(dx)) {
        // Leftward or vertical → it's a scroll, not a back-swipe. Bail.
        this.tracking = false;
        return;
      }
      this.dragging = true;
    }

    // We own the gesture: stop the page (and any native edge-back) from scrolling.
    e.preventDefault();

    const dt = e.timeStamp - this.lastT;
    if (dt > 0) this.velocity = (t.clientX - this.lastX) / dt;
    this.lastX = t.clientX;
    this.lastT = e.timeStamp;

    // Mild resistance past the commit point so it feels elastic, not unbounded.
    const raw = Math.max(0, dx);
    const commit = this.commitPx();
    const eased = raw <= commit ? raw : commit + (raw - commit) * 0.35;
    this.progress = Math.min(1, raw / commit);
    this.renderPuck(eased, t.clientY);
  };

  private readonly onEnd = (): void => {
    if (this.dragging) {
      const flick = this.velocity > this.FLICK_VELOCITY && this.lastX - this.startX > this.FLICK_MIN_PX;
      if (this.progress >= 1 || flick) {
        this.commitPuck();
        this.location.back();
      } else {
        this.cancelPuck();
      }
    }
    this.tracking = false;
    this.dragging = false;
  };

  // ─── Puck (gesture feedback) ──────────────────────────────────────────────

  private ensurePuck(screen: HTMLElement): void {
    if (this.puck?.isConnected) return;
    const el = this.doc.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = [
      'position:absolute',
      'left:0',
      'top:0',
      'z-index:6',
      'width:44px',
      'height:44px',
      'border-radius:50%',
      'display:grid',
      'place-items:center',
      'pointer-events:none',
      'opacity:0',
      'color:var(--accent, #2563eb)',
      'background:var(--bg-elev, #fff)',
      'box-shadow:0 6px 20px rgba(0,0,0,0.18)',
      'will-change:transform,opacity',
    ].join(';');
    el.innerHTML =
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M15 18l-6-6 6-6"/></svg>';
    screen.appendChild(el);
    this.puck = el;
  }

  private renderPuck(offset: number, y: number): void {
    const p = this.puck;
    if (!p) return;
    // Slide in from off-left to track the pull; centre vertically on the finger.
    const x = -54 + offset;
    p.style.transition = 'none';
    p.style.opacity = String(Math.min(1, this.progress * 1.4));
    p.style.transform = `translate(${x}px, ${y - 22}px) scale(${0.7 + this.progress * 0.3})`;
  }

  private commitPuck(): void {
    const p = this.puck;
    if (!p) return;
    p.style.transition = 'transform 160ms ease, opacity 160ms ease';
    p.style.transform = p.style.transform.replace(/scale\([^)]*\)/, 'scale(1.15)');
    p.style.opacity = '0';
  }

  private cancelPuck(): void {
    const p = this.puck;
    if (!p) return;
    p.style.transition = 'transform 180ms ease, opacity 180ms ease';
    const y = p.style.transform.match(/translate\([^,]+,\s*([^)]+)\)/)?.[1] ?? '0px';
    p.style.transform = `translate(-54px, ${y}) scale(0.7)`;
    p.style.opacity = '0';
  }
}
